// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

const StyledWrapperX = getStyledWrapper("div") as any;

const enum fields {
    showDescription = "showDescription",
    showDocuments = "showDocuments",
}

interface Props extends WidgetProps, HasCategoryContext {
    fields: {
        [fields.showDescription]: boolean;
        [fields.showDocuments]: boolean;
    };
}

export interface CategoryDetailsDescriptionStyles {
    container?: InjectableCss;
}

export const categoryDetailsDescriptionStyles: CategoryDetailsDescriptionStyles = {
    container: {
        css: css`
            display: flex;
            flex-wrap: nowrap;
            align-items: flex-start;
            margin-bottom: 45px;

            ${({ hasDocuments }: any) =>
                hasDocuments &&
                css`
                    > div:first-child {
                        margin-right: 30px;
                    }
                `}

            ${xScreen.lte("sm")} {
                flex-wrap: wrap;
            }
        `,
    },
};

const styles = categoryDetailsDescriptionStyles;

const CategoryDetailsDescription: React.FC<Props> = ({ category, fields }: Props) => {
    if (!category) {
        return null;
    }

    const hasDocuments = !!category.properties["documents"];

    return (
        <StyledWrapperX {...styles.container} hasDocuments={hasDocuments}>
            {fields.showDescription && (
                <>
                    {createWidgetElement("Category/CategoryDetailBanner", {
                        fields: {},
                    })}
                </>
            )}
            {fields.showDocuments && hasDocuments && (
                <>
                    {createWidgetElement("Category/CategoryDocuments", {
                        fields: {},
                    })}
                </>
            )}
        </StyledWrapperX>
    );
};

const widgetModule: WidgetModule = {
    component: withCategory(CategoryDetailsDescription),
    definition: {
        group: "Categories",
        icon: "Banner",
        fieldDefinitions: [
            {
                name: fields.showDescription,
                displayName: "Show category description",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showDocuments,
                displayName: "Show category documents",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
